export type LittleWomenPart = "first" | "second";

export type LittleWomenChapter = {
  part: LittleWomenPart;
  title: string;
  body: string;
};

const CHAPTER_RE =
  /<<<CHAPTER part=(first|second) title=(.+?)>>>\n([\s\S]*?)(?=\n<<<CHAPTER |$)/g;

/** Parse the marked Little Women seed text into ordered chapters. */
export function parseLittleWomenChapters(source: string): LittleWomenChapter[] {
  const chapters: LittleWomenChapter[] = [];
  for (const match of source.matchAll(CHAPTER_RE)) {
    const part = match[1] as LittleWomenPart;
    const title = (match[2] ?? "").trim();
    const body = (match[3] ?? "").trim();
    if (title === "" || body === "") continue;
    chapters.push({ part, title, body });
  }
  if (chapters.length !== 47) {
    throw new Error(
      `Expected 47 Little Women chapters, parsed ${chapters.length}`,
    );
  }
  return chapters;
}

/** Lazy-load the committed seed asset (separate Vite chunk). */
export async function loadLittleWomenSource(): Promise<string> {
  const mod = await import("./little-women.txt?raw");
  return mod.default;
}

/**
 * Split prose into N scenes at paragraph boundaries, balancing character
 * weight so showcase chapters get multiple outline/storyboard cards.
 */
export function splitBodyIntoScenes(body: string, count: number): string[] {
  if (count <= 1) return [body.trim()];
  const paras = body
    .split(/\n\n+/)
    .map((p) => p.trim())
    .filter((p) => p !== "");
  if (paras.length <= count) {
    const scenes = [...paras];
    while (scenes.length < count) scenes.push("");
    return scenes.map((p) => p.trim()).filter((p, i) => p !== "" || i === 0);
  }

  const total = paras.reduce((n, p) => n + p.length, 0);
  const target = total / count;
  const scenes: string[] = [];
  let bucket: string[] = [];
  let weight = 0;

  for (let i = 0; i < paras.length; i++) {
    const p = paras[i]!;
    bucket.push(p);
    weight += p.length;
    const remainingParas = paras.length - i - 1;
    const remainingScenes = count - scenes.length - 1;
    if (
      scenes.length < count - 1 &&
      weight >= target &&
      remainingParas >= remainingScenes
    ) {
      scenes.push(bucket.join("\n\n"));
      bucket = [];
      weight = 0;
    }
  }
  if (bucket.length > 0) scenes.push(bucket.join("\n\n"));
  while (scenes.length < count) {
    const last = scenes.pop() ?? "";
    const parts = last.split(/\n\n+/);
    if (parts.length < 2) {
      scenes.push(last);
      break;
    }
    const lastPart = parts.at(-1);
    scenes.push(
      parts.slice(0, -1).join("\n\n"),
      typeof lastPart !== "undefined" ? lastPart : "",
    );
  }
  return scenes.map((s) => s.trim()).filter((s) => s !== "");
}

/** Insert a comment marker immediately before the first occurrence of `needle`. */
export function insertCommentMarker(
  body: string,
  needle: string,
  commentId: string,
): string {
  const idx = body.indexOf(needle);
  if (idx < 0) {
    throw new Error(`Comment needle not found: ${JSON.stringify(needle)}`);
  }
  return `${body.slice(0, idx)}<!-- zw-c:${commentId} -->${body.slice(idx)}`;
}
